import React,{useCallback,useEffect,useState} from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faStar, faUsers, faClock, faPlay, 
  faCheckCircle, faDatabase, faServer, 
  faCloud, faCode, faProjectDiagram,
  faLaptopCode, faCertificate, faBriefcase
} from '@fortawesome/free-solid-svg-icons';
import './HeroSection.css';

const DataEngineering = () => {
    const [showModal, setShowModal] = useState(false);
      const [formCourseName, setFormCourseName] = useState('Data Engineering');
      const [formData, setFormData] = useState({
        fullName: '',
        mobileNumber: '',
        email: '',
      });
    
      const openModal = () => setShowModal(true);
      const closeModal = useCallback(() => {
        setShowModal(false);
        setFormData({ fullName: '', mobileNumber: '', email: '' });
      }, []);
    
      useEffect(() => {
        const handleEsc = (e) => {
          if (e.key === 'Escape') closeModal();
        };
        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
      }, [closeModal]);
    
      const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name === 'mobileNumber') {
          const digitsOnly = value.replace(/\D/g, '');
          if (digitsOnly.length <= 10) {
            setFormData(prev => ({ ...prev, [name]: digitsOnly }));
          }
        } else {
          setFormData(prev => ({ ...prev, [name]: value }));
        }
      };
    
      const handleSubmit = async (e) => {
        e.preventDefault();
        const { fullName, mobileNumber, email } = formData;
    
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
        if (!fullName || !emailRegex.test(email) || mobileNumber.length !== 10) {
          alert('Please fill in valid details.');
          return;
        }
    
        try {
          const response = await fetch('https://api.dikshasea.in/api/enrollments', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ...formData, course: formCourseName }),
          });
    
          const result = await response.json();
          if (response.ok) {
            alert('Enrollment successful!');
            closeModal();
          } else {
            alert(result.message || 'Submission failed');
          }
        } catch (err) {
          console.error(err);
          alert('Error submitting form');
        }
      };
    
  return (
    <>
      <section className="hero-section">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <span className="badge bg-primary bg-opacity-10 text-white mb-3">New Data Engineering Course</span>
              <h1 className="hero-title">
                Master <span className="hero-highlight">Data Engineering</span>
              </h1>
              <p className="hero-subtitle">
                Build robust data pipelines and infrastructure with our comprehensive program. 
                Gain expertise in big data technologies, ETL processes, and cloud data platforms 
                through hands-on projects.
              </p>
              
              <div className="course-meta">
                <div className="meta-item">
                  <div className="meta-icon">
                    <FontAwesomeIcon icon={faStar} />
                  </div>
                  <div>
                    <div className="fw-bold">4.9/5.0</div>
                    <small className="text-muted">(3,500 reviews)</small>
                  </div>
                </div>
                <div className="meta-item">
                  <div className="meta-icon">
                    <FontAwesomeIcon icon={faUsers} />
                  </div>
                  <div>
                    <div className="fw-bold">15,000+</div>
                    <small className="text-muted">Students</small>
                  </div>
                </div>
                <div className="meta-item">
                  <div className="meta-icon">
                    <FontAwesomeIcon icon={faClock} />
                  </div>
                  <div>
                    <div className="fw-bold">65 hours</div>
                    <small className="text-muted">Content</small>
                  </div>
                </div>
              </div>
              
             <button className="btn btn-primary btn-lg px-4" onClick={openModal}>
                  Enroll Now
                </button>
            </div>
            <div className="col-lg-6 mt-5 mt-lg-0">
              <div className="ratio ratio-16x9">
                <img 
                  src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80" 
                  alt="Data Engineering Course" 
                  className="img-fluid rounded-3 shadow"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="container">
        <div className="course-details">
          <div className="course-content">
            {/* About Course */}
            <section className="about-course">
              <h2 className="section-title">About This Course</h2>
              <p>This comprehensive Data Engineering program will take you from database fundamentals to building scalable data pipelines. You'll learn to design data warehouses, implement ETL processes, and work with big data technologies like Spark and Hadoop.</p>
              <p>Through real-world projects, you'll gain experience with cloud platforms (AWS, GCP, Azure) and modern data infrastructure tools.</p>
            </section>
            
            {/* Curriculum */}
            <section className="curriculum">
              <h2 className="section-title">Curriculum</h2>
              
              <div className="curriculum-item">
                <h3><FontAwesomeIcon icon={faDatabase} className="me-2" /> Module 1: Data Systems Fundamentals</h3>
                <p>Master the core concepts of data engineering and architecture</p>
                <div className="topics">
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Database design and modeling</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> SQL and NoSQL systems</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Data warehousing concepts</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> ETL vs ELT patterns</div>
                </div>
              </div>
              
              <div className="curriculum-item">
                <h3><FontAwesomeIcon icon={faServer} className="me-2" /> Module 2: Big Data Technologies</h3>
                <p>Become proficient in distributed systems and processing</p>
                <div className="topics">
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Hadoop ecosystem</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Apache Spark (PySpark)</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Distributed storage (HDFS, S3)</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Stream processing (Kafka, Flink)</div>
                </div>
              </div>
              
              <div className="curriculum-item">
                <h3><FontAwesomeIcon icon={faCloud} className="me-2" /> Module 3: Cloud Data Platforms</h3>
                <p>Develop expertise in cloud-based data solutions</p>
                <div className="topics">
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> AWS (Redshift, Glue, EMR)</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Google Cloud (BigQuery, Dataflow)</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Azure (Synapse, Data Factory)</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Cloud data warehousing</div>
                </div>
              </div>
              
              <div className="curriculum-item">
                <h3><FontAwesomeIcon icon={faCode} className="me-2" /> Module 4: Pipeline Development</h3>
                <p>Design and implement production-grade data pipelines</p>
                <div className="topics">
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Airflow for orchestration</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Data quality and testing</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Monitoring and alerting</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Infrastructure as Code (Terraform)</div>
                </div>
              </div>
              
              <div className="curriculum-item">
                <h3><FontAwesomeIcon icon={faProjectDiagram} className="me-2" /> Module 5: Capstone Project</h3>
                <p>Build an end-to-end data platform with real-world datasets</p>
                <div className="topics">
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Data ingestion pipeline</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Transformation and processing</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Storage and serving layer</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Performance optimization</div>
                </div>
              </div>
            </section>
          </div>  
          
          <div className="course-sidebar">
            <div className="info-item">
              <div className="info-icon">
                <FontAwesomeIcon icon={faClock} />
              </div>
              <div className="info-content">
                <h4>Duration</h4>
                <p>14 Weeks (20 hrs/week)</p>
              </div>
            </div>
            
            <div className="info-item">
              <div className="info-icon">
                <FontAwesomeIcon icon={faCertificate} />
              </div>
              <div className="info-content">
                <h4>Level</h4>
                <p>Intermediate to Advanced</p>
              </div>
            </div>
            
            <div className="info-item">
              <div className="info-icon">
                <FontAwesomeIcon icon={faLaptopCode} />
              </div>
              <div className="info-content">
                <h4>Projects</h4>
                <p>6 Real-world Projects</p>
              </div>
            </div>
            
            <div className="info-item">
              <div className="info-icon">
                <FontAwesomeIcon icon={faUsers} />
              </div>
              <div className="info-content">
                <h4>Prerequisites</h4>
                <p>Python, SQL, Basic Linux</p>
              </div>
            </div>
            
            <div className="info-item">
              <div className="info-icon">
                <FontAwesomeIcon icon={faBriefcase} />
              </div>
              <div className="info-content">
                <h4>Certifications</h4>
                <p>AWS, Azure, GCP prep</p>
              </div>
            </div>
            
            <button className="btn btn-primary btn-lg px-4" onClick={openModal}>
                  Enroll Now
                </button>
          </div>
        </div>
      </div>
      {/* MODAL */}
      {showModal && (
        <div
          className="modal d-block fade show"
          style={{ background: 'rgba(0,0,0,0.5)' }}
          onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content p-4 d-flex flex-column flex-md-row">
              <div className="w-100 w-md-50 mb-3 mb-md-0">
                <img
                  src="/img/enroll.jpg"
                  alt="Enrollment"
                  className="img-fluid h-100"
                  style={{ objectFit: 'cover', width: '100%' }}
                />
              </div>
              <div className="w-100 w-md-50 ps-md-4">
                <h6 className="text-primary">Course:</h6>
                <h4 className="fw-bold">Data Engineering</h4>
                <form onSubmit={handleSubmit} noValidate>
                  <input
                    className="form-control mb-3"
                    type="text"
                    name="fullName"
                    placeholder="Full Name"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    required
                  />
                  <input
                    className="form-control mb-3"
                    type="text"
                    name="mobileNumber"
                    placeholder="Mobile Number"
                    value={formData.mobileNumber}
                    onChange={handleInputChange}
                    required
                    maxLength={10}
                  />
                  <input
                    className="form-control mb-3"
                    type="email"
                    name="email"
                    placeholder="Email Address"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                  <div className="d-flex justify-content-end">
                    <button type="button" className="btn btn-outline-dark me-2" onClick={closeModal}>
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary">Submit</button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DataEngineering;